import { API_ENDPOINTS } from '../config/api'

function getAuthHeaders(): HeadersInit {
  const token =
    typeof window !== 'undefined'
      ? localStorage.getItem('token') || localStorage.getItem('accessToken')
      : null
  return token ? { Authorization: `Bearer ${token}` } : {}
}

export type TopologyControl = {
  controlCode: string
  controlId: string
  controlTitle?: string
  applicabilityStatus: string
  scopeType: string
  source?: string
  explanations?: Array<{ type: string; message: string }>
  implementationStatus?: string | null
}

export type ApplicabilityPayload = {
  standardCode: string
  applicableControls: TopologyControl[]
  organizationControls: TopologyControl[]
  scopedControls: TopologyControl[]
  inheritedControls: TopologyControl[]
  metrics?: {
    readinessPercent: number | null
    evidenceCoveragePercent: number | null
    vulnerabilityPressure?: number
    openRiskCount?: number
  }
}

async function parseJson(res: Response) {
  const body = await res.json()
  if (!res.ok) throw new Error(body?.message || body?.error || 'Request failed')
  return body?.data ?? body
}

export async function fetchApplicabilitySummary(
  systemId: string,
  standardCode: string,
  organizationId?: string | null
) {
  const url = API_ENDPOINTS.COMPLIANCE_TOPOLOGY_IS_SUMMARY(systemId, standardCode, organizationId)
  const res = await fetch(url, { headers: getAuthHeaders() })
  return parseJson(res) as ApplicabilityPayload
}

export async function fetchApplicability(
  systemId: string,
  standardCode: string,
  organizationId?: string | null
) {
  const url = API_ENDPOINTS.COMPLIANCE_TOPOLOGY_IS_APPLICABILITY(systemId, standardCode, organizationId)
  const res = await fetch(url, { headers: getAuthHeaders() })
  return parseJson(res) as ApplicabilityPayload
}

export async function fetchApplicabilityExplanation(
  systemId: string,
  standardCode: string,
  controlCode: string,
  organizationId?: string | null
) {
  const url = API_ENDPOINTS.COMPLIANCE_TOPOLOGY_IS_EXPLANATIONS(
    systemId,
    standardCode,
    controlCode,
    organizationId
  )
  const res = await fetch(url, { headers: getAuthHeaders() })
  return parseJson(res)
}

export type ControlStateRow = {
  controlCode: string | null
  operationalState: string
  evidenceCoveragePercent?: number | null
  operationalPressure?: number | null
  staleEvidenceCount?: number | null
  reasoning?: { summary?: string; explanations?: Array<{ type: string; message: string }> }
}

export type EvidenceFreshnessRow = {
  evidenceId: string
  freshnessStatus: string
  freshnessPercent?: number | null
  rationale?: string | null
  evidenceTitle?: string
}

export type CertificationBlocker = {
  id?: string
  blockerType: string
  severity: string
  title: string
  description: string
}

export type ComplianceDriftRow = {
  id?: string
  driftType: string
  severity: string
  description: string
  detectedAt?: string
}

export type MaterializedReadinessPayload = {
  materializedEnabled?: boolean
  standardCode: string
  readiness: {
    readinessStatus: string
    organizationReadiness?: number | null
    scopedReadiness?: number | null
    inheritedReadiness?: number | null
    operationalReadiness?: number | null
    evidenceCoverage?: number | null
    operationalPressure?: number | null
    blockerCount?: number
    explanations?: Array<{ type: string; message: string; title?: string }>
    computedAt?: string
  } | null
  controlStates: ControlStateRow[]
  evidenceFreshness: EvidenceFreshnessRow[]
  blockers: CertificationBlocker[]
  drift: ComplianceDriftRow[]
}

export async function fetchMaterializedReadiness(
  systemId: string,
  standardCode: string,
  organizationId?: string | null
) {
  const url = API_ENDPOINTS.COMPLIANCE_TOPOLOGY_IS_READINESS(systemId, standardCode, organizationId)
  const res = await fetch(url, { headers: getAuthHeaders() })
  return parseJson(res) as MaterializedReadinessPayload
}
